import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Payment, PaymentStatus, Project } from '../../types';
import { useI18n } from '../../context/I18nContext';
import { useTranslations } from '../../hooks/useTranslations';
import { useTableSelection } from '../../hooks/useTableSelection';
import Table, {
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableSelectionHeader,
  TableSelectionCell,
  TableActionsHeader,
  TableActionsCell,
  TableEmptyRow,
  tableRowSelectedClass,
} from '../ui/Table';
import ActionIconButton from '../ui/ActionIconButton';
import ContextMenu, { ContextMenuTarget } from '../ui/ContextMenu';
import { Edit, Trash2, DollarSign, ExternalLink, CreditCard, FileText } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import TruncateTooltip from '../ui/TruncateTooltip';
import InfiniteLoadMore from '../ui/InfiniteLoadMore';
import EmptyState from '../ui/EmptyState';
import { clickableLinks, paymentStatusColors, neutralColors } from '../../styles/colors';
import { navigateToProject } from '../../utils/navigationUtils';
import type { Invoice } from '../../types';
import { TABLE_QUICK_ACTION_CLASS, appendEditDeleteMenuItems, useQuickEditDeleteVisible } from '../ui/tableActions';
import { ENABLE_INVOICES } from '../../config/featureFlags';

interface PaymentTableProps {
  payments: Payment[];
  projects: Project[];
  /** Map paymentId → invoice when linked */
  invoicesByPaymentId?: Record<string, Invoice>;
  onEdit: (payment: Payment) => void;
  onDelete: (id: string) => void;
  onMarkAsPaid?: (payment: Payment) => void;
  onAddInvoice?: (payment: Payment) => void;
  onOpenInvoice?: (payment: Payment, invoice: Invoice) => void;
  onBulkDelete?: (ids: string[]) => void;
  onRegisterToggleAll?: (toggleAll: () => void) => void;
  onRegisterBulkDelete?: (bulkDelete: () => void) => void;
  onRegisterClearSelection?: (clearSelection: () => void) => void;
  onSelectedCountChange?: (count: number) => void;
  onIsAllSelectedChange?: (isAll: boolean) => void;
  onSelectedPaymentsChange?: (payments: Payment[]) => void;
  hideHeaderCheckbox?: boolean;
  hideRowCheckboxes?: boolean;
  hideBulkActionsBar?: boolean;
  // Infinite loading (Notion-like)
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  isFilteredEmpty?: boolean;
  onCreate?: () => void;
}

const PaymentTable: React.FC<PaymentTableProps> = ({
  payments,
  projects,
  invoicesByPaymentId = {},
  onEdit,
  onDelete,
  onMarkAsPaid,
  onAddInvoice,
  onOpenInvoice,
  onBulkDelete,
  onRegisterToggleAll,
  onRegisterBulkDelete,
  onRegisterClearSelection,
  onSelectedCountChange,
  onIsAllSelectedChange,
  onSelectedPaymentsChange,
  hideHeaderCheckbox = false,
  hideRowCheckboxes = false,
  hideBulkActionsBar = false,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  isFilteredEmpty = false,
  onCreate,
}) => {
  const { t } = useI18n();
  const { formatDateShort, formatCurrency } = useTranslations();
  const navigate = useNavigate();
  const showQuickEditDelete = useQuickEditDeleteVisible();
  
  const {
    isAllSelected,
    selectedCount,
    handleSelectAll,
    handleSelectItem,
    isSelected,
  } = useTableSelection<Payment>({
    items: payments,
    onBulkDelete,
    onRegisterToggleAll,
    onRegisterBulkDelete,
    onRegisterClearSelection,
    onSelectedCountChange,
    onIsAllSelectedChange,
    onSelectedItemsChange: onSelectedPaymentsChange,
  });

  // Function to navigate to project details
  const handleViewProject = (projectId: string) => {
    navigateToProject(navigate, projectId);
  };

  const getStatusLabel = (status: PaymentStatus): string => {
    switch (status) {
      case 'sent':
        return t('payments.statusLabels.sent');
      case 'paid':
        return t('payments.statusLabels.paid');
      case 'overdue':
        return t('payments.statusLabels.overdue');
      default:
        return status;
    }
  };

  const formatDateCompact = (dateValue: string | Date): string => {
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' });
  };

  const isOverdue = (payment: Payment): boolean => {
    if (payment.status === 'overdue') return true;
    if (payment.status === 'sent') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dueDate = new Date(payment.dueDate);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate < today;
    }
    return false;
  };

  const columnCount = ENABLE_INVOICES ? 8 : 7;

  const emptyState = isFilteredEmpty ? (
    <EmptyState
      icon={CreditCard}
      title={t('payments.noPaymentsFound')}
      description={t('common.tryAdjustingFilters')}
    />
  ) : (
    <EmptyState
      icon={CreditCard}
      title={t('payments.noPayments')}
      description={t('payments.noPaymentsDescription')}
      actionLabel={onCreate ? t('payments.createFirst') : undefined}
      onAction={onCreate}
    />
  );

  return (
    <div className="space-y-4">
      <div className="border border-gray-200/60 rounded-xl shadow-sm bg-white overflow-hidden">
        <Table className="border-0 rounded-none" dataTableColumnCount={columnCount}>
          <TableHeader>
            <TableRow>
              <TableSelectionHeader
                isAllSelected={isAllSelected}
                selectedCount={selectedCount}
                onSelectAll={handleSelectAll}
                selectAllAriaLabel={t('projects.selectAll')}
                hidden={hideHeaderCheckbox || payments.length === 0}
              />
              <TableHead className="pr-2 lg:pr-3">{t('payments.project')}</TableHead>
              {ENABLE_INVOICES ? (
                <TableHead className="w-11 sm:w-28 pl-2 lg:pl-3 pr-1 lg:pr-2 text-center">
                  <span className="sm:hidden">{t('payments.invoiceShort')}</span>
                  <span className="hidden sm:inline">{t('payments.invoice')}</span>
                </TableHead>
              ) : null}
              <TableHead className="text-center hidden lg:table-cell">{t('payments.issueDate')}</TableHead>
              <TableHead className="text-center pl-1 lg:pl-2">
                <span className="sm:hidden">{t('payments.dueShort')}</span>
                <span className="hidden sm:inline">{t('payments.dateLabel')}</span>
              </TableHead>
              <TableHead className="text-center hidden sm:table-cell">{t('payments.status')}</TableHead>
              <TableHead className="text-center">{t('payments.amount')}</TableHead>
              <TableActionsHeader label={t('common.actions')} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.length === 0 ? (
              <TableEmptyRow colSpan={columnCount}>{emptyState}</TableEmptyRow>
            ) : (
              payments.map((payment) => (
              <ContextMenuTarget<HTMLTableRowElement> key={payment.id}>
                {(rowRef) => (
              <TableRow
                ref={rowRef}
                data-payment-id={payment.id}
                className={isSelected(payment.id) ? tableRowSelectedClass : ''}
                onClick={hideRowCheckboxes ? undefined : () => onEdit(payment)}
              >
                <TableSelectionCell
                  itemId={payment.id}
                  isSelected={isSelected(payment.id)}
                  onToggle={handleSelectItem}
                  hidden={hideRowCheckboxes}
                />
                <TableCell className="font-medium min-w-0 pr-2 lg:pr-3">
                  {(() => {
                    const project = projects.find((p) => p.id === payment.projectId);
                    if (project) {
                      return (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewProject(payment.projectId);
                          }}
                          className={`${clickableLinks} hover:underline transition-colors duration-200 w-full min-w-0 text-left`}
                        >
                          <TruncateTooltip text={project.name} />
                        </button>
                      );
                    }
                    return (
                      <span className="text-gray-500 italic">
                        {t('finances.deletedProject')}
                      </span>
                    );
                  })()}
                </TableCell>
                {ENABLE_INVOICES ? (
                <TableCell className="min-w-0 w-11 sm:w-28 pl-2 lg:pl-3 pr-1 lg:pr-2 text-center">
                  {(() => {
                    const invoice = invoicesByPaymentId[payment.id];
                    if (invoice) {
                      return (
                        <Tooltip content={t('payments.viewInvoicePdfTooltip')}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpenInvoice?.(payment, invoice);
                            }}
                            className={`${clickableLinks} hover:underline inline-flex items-center justify-center gap-1 min-w-0 max-w-full`}
                            aria-label={`${invoice.invoiceNumber} — ${t('payments.viewInvoicePdfTooltip')}`}
                          >
                            <span className="hidden sm:inline truncate">
                              {invoice.invoiceNumber}
                            </span>
                            <FileText className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
                          </button>
                        </Tooltip>
                      );
                    }
                    return (
                      <Tooltip content={t('payments.addInvoiceTooltip')}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onAddInvoice?.(payment);
                          }}
                          className={`${clickableLinks} hover:underline inline-flex items-center justify-center text-sm font-medium w-full`}
                          aria-label={t('payments.addInvoiceTooltip')}
                        >
                          <span className="sm:hidden">{t('payments.addInvoiceShort')}</span>
                          <span className="hidden sm:inline">{t('payments.addInvoice')}</span>
                        </button>
                      </Tooltip>
                    );
                  })()}
                </TableCell>
                ) : null}
                <TableCell className="text-center hidden lg:table-cell">
                  {formatDateShort(payment.issueDate)}
                </TableCell>
                <TableCell className="text-center pl-1 lg:pl-2">
                  {payment.status === 'paid' && payment.paymentDate ? (
                    <Tooltip content={t('payments.paymentDate')}>
                      <span className="text-green-700 font-medium">
                        <span className="sm:hidden">{t('payments.paidShort') || 'Payé'} {formatDateCompact(payment.paymentDate)}</span>
                        <span className="hidden sm:inline">{t('payments.paidOn')} {formatDateShort(payment.paymentDate)}</span>
                      </span>
                    </Tooltip>
                  ) : (
                    <Tooltip content={t('payments.dueDate')}>
                      <span className={isOverdue(payment) ? 'text-red-600 font-medium' : ''}>
                        <span className="sm:hidden">{t('payments.dueShort') || 'Éch.'} {formatDateCompact(payment.dueDate)}</span>
                        <span className="hidden sm:inline">{t('payments.dueOn')} {formatDateShort(payment.dueDate)}</span>
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell className="text-center hidden sm:table-cell">
                  <span className={`inline-flex items-center font-medium rounded-full px-3 py-1 text-sm ${paymentStatusColors[payment.status].badge || neutralColors.default}`}>
                    {getStatusLabel(payment.status)}
                  </span>
                </TableCell>
                <TableCell className="text-center font-medium">
                  {formatCurrency(payment.amount, payment.currency || 'EUR')}
                </TableCell>
                <TableActionsCell>
                  <ContextMenu
                    contextTargetRef={rowRef}
                    items={(() => {
                      const items: Array<{ label: string; onClick: () => void; icon?: any }> = [];
                      if (onMarkAsPaid && payment.status !== 'paid') {
                        items.push({
                          label: t('payments.markAsPaid'),
                          onClick: () => onMarkAsPaid(payment),
                          icon: DollarSign,
                        });
                      }
                      if (payment.projectId) {
                        items.push({
                          label: t('payments.viewProject'),
                          onClick: () => handleViewProject(payment.projectId),
                          icon: ExternalLink,
                        });
                      }
                      appendEditDeleteMenuItems({
                        items,
                        t,
                        onEdit: () => onEdit(payment),
                        onDelete: () => onDelete(payment.id),
                        omitFromMenu: showQuickEditDelete,
                      });
                      return items;
                    })()}
                    trigger="more"
                    size="sm"
                    tooltip={t('common.moreActions')}
                  />
                  <span className={TABLE_QUICK_ACTION_CLASS}>
                    <Tooltip content={t('common.edit')}>
                      <ActionIconButton
                        icon={Edit}
                        variant="edit"
                        size="sm"
                        onClick={() => onEdit(payment)}
                      />
                    </Tooltip>
                  </span>
                  <span className={TABLE_QUICK_ACTION_CLASS}>
                    <Tooltip content={t('common.delete')}>
                      <ActionIconButton
                        icon={Trash2}
                        variant="delete"
                        size="sm"
                        onClick={() => onDelete(payment.id)}
                      />
                    </Tooltip>
                  </span>
                </TableActionsCell>
              </TableRow>
                )}
              </ContextMenuTarget>
              ))
            )}
          </TableBody>
        </Table>
        
        {/* Infinite load more */}
        {onLoadMore && (
          <InfiniteLoadMore
            hasMore={hasMore}
            loading={loadingMore}
            hasItems={payments.length > 0}
            onLoadMore={onLoadMore}
          />
        )}
      </div>
    </div>
  );
};

export default PaymentTable;
